import { useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AuthContext } from '@/context/AuthContext';
import { ThemeContext } from '@/context/ThemeContext';
import { BrandRevealContext } from '@/context/BrandRevealContext';
import { api } from '@/lib/api';
import type { Make, ReferenceData } from '@/lib/types';

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }

  return context;
}

export function useTheme() {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error('useTheme must be used inside ThemeProvider');
  }

  return context;
}

export function useBrandReveal() {
  const context = useContext(BrandRevealContext);

  if (!context) {
    throw new Error('useBrandReveal must be used inside BrandRevealProvider');
  }

  return context;
}

/**
 * The whole reference catalogue: makes, models, cities, and every enum with its Serbian label.
 *
 * Fetched once and then treated as immutable, because it only changes when the site is deployed.
 * React Query's cache makes every consumer share the single response, so the filter panel, the
 * listing form and the spec table on a detail page cost one request between them rather than three.
 */
export function useReference() {
  return useQuery({
    queryKey: ['reference'],
    queryFn: () => api<ReferenceData>('/api/reference'),
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

/**
 * Makes indexed by display name.
 *
 * Listing payloads carry `makeName` but not the make's id or slug, and the brand badge needs the
 * slug to find the marque's colour. Looking it up here beats widening the listing DTO for the sake
 * of a decoration, and the catalogue is already in memory anyway.
 */
export function useMakeLookup(): Map<string, Make> {
  const { data: reference } = useReference();

  return useMemo(() => {
    const byName = new Map<string, Make>();

    for (const make of reference?.makes ?? []) {
      byName.set(make.name, make);
    }

    return byName;
  }, [reference]);
}
